<template>
  <div
    class="action-bar"
    :class="{ 'action-bar--dark-mode': isLoggedStaffOnCall }"
    :data-theme="isLoggedStaffOnCall ? 'on-dark' : 'on-light'"
  >
    <div v-for="item in visibleMenus" :key="item.id">
      <component
        :is="getMenuConfig(item).component"
        v-if="getMenuConfig(item).component"
      />
      <ActionBarLink
        v-else
        :config="getMenuConfig(item)"
        @click="logAction(item)"
      />
    </div>

    <UpdateApp :feature-flag-version="featureFlagVersion" />

    <footer class="action-bar__footer">
      <div
        class="action-bar__item action-bar__item--certification"
        @mouseenter="updateCertificationIndicators"
      >
        <img
          v-if="!isUserAllowToUpdate"
          :src="user.profileImageUrl"
          class="action-bar__user"
          alt=""
        />
        <HUserMenu v-else :certification-state="certificationState">
          <WTooltip
            :label="userTooltip"
            class="action-bar__certification-tooltip"
            position="right"
          >
            <div
              :data-certification-state="certificationState"
              :data-user-state="userStatus"
              class="action-bar__user-box"
              data-dd-action-name="action_bar_certification"
            >
              <img
                :src="user.profileImageUrl"
                class="action-bar__user"
                alt=""
              />
            </div>
          </WTooltip>
        </HUserMenu>
      </div>

      <CHealthProfessionalOncallToggle v-if="isUserAllowedToOnCall" />

      <WTooltip label="Sair" position="right">
        <div class="action-bar__item action-bar__button">
          <WButton
            v-if="isStencilMultiWordMounted"
            :loading="isLoginOut"
            :disabled="isLoginOut"
            icon="icLogout"
            size="large"
            variant="tertiary"
            icon-button
            @click="logout"
          />
        </div>
      </WTooltip>
    </footer>
  </div>
</template>

<script>
import { WButton, WIcon, WTooltip } from '@alice-health/wonderland-vue'
import { mapGetters, mapState } from 'vuex'

import CHealthProfessionalOncallToggle from '@/components/channels/molecules/CHealthProfessionalOncallToggle.vue'
import { STAFF_STATUS_LABEL } from '@/models/channels'
import { MENU_TYPE } from '@/models/menu'
import { useAuthService } from '@/modules/auth'
import {
  CertificationStatus,
  HUserMenu,
  useCertificationState
} from '@/modules/user'
import Logger, { EventName } from '@/wrappers/logger'

import { ActionBarLink } from '../../atoms'
import UpdateApp from '../../atoms/UpdateApp.vue'
import { MENU_LINK_CONFIG } from './config'

export default {
  name: 'ActionBar',
  components: {
    CHealthProfessionalOncallToggle,
    ActionBarLink,
    HUserMenu,
    WIcon,
    WTooltip,
    WButton,
    UpdateApp
  },
  data() {
    return {
      isLoginOut: false,
      isStencilMultiWordMounted: false, //DS: Workaraund multi-word stencil components load with App.vue
      certificationTooltipMap: {
        [CertificationStatus.ACTIVE]: 'Certificado digital ativo',
        [CertificationStatus.NEAR_EXPIRATION]:
          'Sua sessão expira em 24h.<br>Clique para renovar.',
        [CertificationStatus.INACTIVE]: 'Certificado digital desligado'
      },
      certificationTooltip: '',
      certificationState: CertificationStatus.INACTIVE
    }
  },
  computed: {
    ...mapGetters('user', ['user', 'hasPermissionToGetChat']),
    ...mapGetters('channel/staff', ['loggedStaff', 'isLoggedStaffOnCall']),
    ...mapGetters('user/certification', ['certification']),
    ...mapGetters('featureConfig', ['getValue']),
    ...mapState('permissions', {
      visibleMenus: state => state.menu.visibleMenus || [],
      isUserAllowedToOnCall: state => state.user.onCall.view,
      isUserAllowToUpdate: state => state.user.general.update
    }),
    userStatus() {
      return this.hasPermissionToGetChat ? this.loggedStaff?.status : ''
    },
    userTooltip() {
      const statusTooltip =
        STAFF_STATUS_LABEL[this.loggedStaff?.status] + ' &#8226; '
      return this.hasPermissionToGetChat
        ? statusTooltip + this.certificationTooltip
        : this.certificationTooltip
    },
    featureFlagVersion() {
      return this.getValue('ehr-app_version')
    }
  },
  watch: {
    'certification.token'() {
      this.updateCertificationIndicators()
    }
  },
  created() {
    this.updateCertificationIndicators()
  },
  mounted() {
    this.isStencilMultiWordMounted = true
  },
  methods: {
    async logout() {
      this.logAction(MENU_TYPE.LOGOUT)
      this.isLoginOut = true
      const authService = useAuthService()
      await authService.logout(true)
      this.isLoginOut = false
    },
    updateCertificationIndicators() {
      if (!this.isUserAllowToUpdate) return
      const { getMappedCertificationState } = useCertificationState()
      this.certificationState = getMappedCertificationState(this.certification)
      this.certificationTooltip =
        this.certificationTooltipMap[this.certificationState]
    },
    logAction(menu) {
      Logger.addLog(EventName.USER_INTERACTION_MENU, {
        data: {
          context: menu
        }
      })
    },
    getMenuConfig(item) {
      return MENU_LINK_CONFIG[item]
    }
  }
}
</script>

<style lang="scss">
.action-bar {
  --background-color: var(--gl-color-shades-white);
  --icon-fill: var(--gl-color-shades-gray-90);
  --icon-fill-selected: var(--gl-color-shades-magenta-60);
  --border-footer-color: var(--gl-color-shades-gray-20);

  background-color: var(--background-color);
  height: 100%;
  border-right-style: solid;
  border-right-width: 1px;
  border-right-color: var(--gl-color-shades-gray-20);
  display: flex;
  flex-direction: column;
  position: relative;

  &--dark-mode {
    --background-color: var(--gl-color-shades-gray-100);
    --icon-fill: var(--gl-color-shades-white);
    --border-footer-color: var(--gl-color-shades-gray-60);
  }

  &__item {
    &--certification {
      position: relative;
    }
  }

  &__certification-tooltip {
    width: 100%;
    height: 100%;
  }

  &__menu {
    background-color: var(--icon-fill);
    mask: url('~@/assets/logos/alice-black-small.svg') no-repeat center;
    height: 64px;
    width: 64px;

    &:hover,
    .action-bar__item--selected & {
      background-color: var(--icon-fill-selected);
    }
  }

  &__healthcare-map {
    background-color: var(--icon-fill);
    mask: url('~@/assets/healthcare-map.svg') no-repeat center;
    height: 64px;
    width: 64px;

    &:hover,
    .action-bar__item--selected & {
      background-color: var(--icon-fill-selected);
    }
  }

  &__user {
    width: 32px;
    height: 32px;
    border-radius: 50%;
  }

  &__user-box {
    position: relative;
    width: 44px;
    height: 44px;
    padding: var(--gl-spacing-01);
    border-radius: var(--gl-border-radius-pill);

    &::after {
      position: absolute;
      top: 28px;
      left: 28px;
      z-index: 2;
      content: '';
      width: 16px;
      height: 16px;
      border-radius: var(--gl-border-radius-pill);
      border: var(--gl-border-width-md) solid var(--gl-color-shades-white);
    }

    &[data-certification-state='ACTIVE'] {
      border: var(--gl-border-width-md) solid var(--sys-color-content-success);

      &::after {
        background-color: var(--sys-color-content-success);
      }
    }

    &[data-certification-state='NEAR_EXPIRATION'] {
      border: var(--gl-border-width-md) solid var(--gl-color-shades-yellow-60);

      &::after {
        background-color: var(--gl-color-shades-yellow-60);
      }
    }

    &[data-certification-state='INACTIVE'] {
      border: var(--gl-border-width-md) solid var(--sys-color-content-error);

      &::after {
        background-color: var(--sys-color-content-error);
      }
    }

    &[data-user-state='AVAILABLE'] {
      &::after {
        background-color: var(--sys-color-content-success);
      }
    }
    &[data-user-state='UNAVAILABLE'] {
      &::after {
        background-color: var(--sys-color-content-error);
      }
    }
    &[data-user-state='BUSY'] {
      &::after {
        background-color: var(--gl-color-shades-yellow-60);
      }
    }
  }

  &__footer {
    border-top: 1px solid var(--border-footer-color);
    margin-top: auto;
  }
}

@media (max-width: 425px) {
  .action-bar {
    flex-direction: row;
    height: auto;

    &__footer {
      display: flex;
      flex-direction: row;
      border: none;
    }
  }
}
</style>
